import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Clock, GitCompare, Link2, Info, AlertTriangle, Check } from 'lucide-react';
import Sphere360Viewer from './Sphere360Viewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONF_STYLE = { HIGH: 'bg-emerald-100 text-emerald-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-slate-100 text-slate-600' };
const MATCH_LABEL = {
  EXACT_LOGICAL_LOCATION: 'Same logical capture location', USER_CONFIRMED_MATCH: 'User-confirmed match',
  FLOOR_PLAN_POINT_MATCH: 'Floor-plan-positioned', CONTEXT_MATCH: 'Context-matched',
  NO_ESTABLISHED_MATCH: 'No established relationship',
};

const panoFile = (projectId, id) => `${API}/projects/${projectId}/panoramas/${id}/file`;

/** Time Travel timeline for a capture point + governed Before/After comparison. */
export default function SiteWalkTimeTravel({ projectId, capturePoint, onClose }) {
  const [hist, setHist] = useState(null);
  const [sel, setSel] = useState(null);            // selected historical capture (Date B)
  const [cmp, setCmp] = useState(null);            // comparison payload
  const [sync, setSync] = useState(true);
  const [mobileSide, setMobileSide] = useState('A');
  const [viewA, setViewA] = useState(null);
  const [viewB, setViewB] = useState(null);
  const [aiOut, setAiOut] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const analyzeChange = async () => {
    if (!cmp) return;
    setAiBusy(true); setAiOut(null);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/sitewalk-ai/analyze`, {
        analysisIntent: 'VISUAL_CHANGE', panoramaId: cmp.paneA.panoramaId, targetPanoramaId: cmp.paneB.panoramaId,
        scope: 'BROADER', matchContext: { matchType: cmp.matchType, confidence: cmp.confidence },
      });
      setAiOut(r.data.reviewStatus || 'PENDING_REVIEW');
    } catch (e) {
      const d = e.response?.data?.detail || '';
      setAiOut('AI_UNAVAILABLE');
      if (e.response?.status && e.response.status !== 400) toast.error(d || 'Analysis failed');
    }
    setAiBusy(false);
  };

  const load = useCallback(async () => {
    try { setHist((await axios.get(`${API}/projects/${projectId}/capture-points/${capturePoint.id}/history`)).data); }
    catch (e) { toast.error('Failed to load history'); }
  }, [projectId, capturePoint.id]);
  useEffect(() => { load(); }, [load]);

  const openCompare = async (other) => {
    setSel(other);
    if (!capturePoint.panoramaId || !other.panoramaId) { toast.error('Both captures need a processed panorama'); return; }
    try {
      const r = await axios.get(`${API}/projects/${projectId}/sitewalk-compare`, { params: { a: capturePoint.panoramaId, b: other.panoramaId } });
      setCmp(r.data); setMobileSide('A'); setViewA(null); setViewB(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Comparison not permitted'); }
  };

  const others = (hist?.history || []).filter((h) => h.capturePointId !== capturePoint.id);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-6" data-testid="timetravel-modal">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><Clock className="w-5 h-5 text-sky-600" /> Visual Time Travel</h3>
          <button onClick={onClose} data-testid="timetravel-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="p-4">
          {/* Timeline */}
          <div className="mb-4">
            <div className="flex items-center gap-1 text-xs text-slate-500 mb-2">
              <Info className="w-3.5 h-3.5" />
              {hist?.basis === 'EXACT_LOGICAL_LOCATION'
                ? 'Captures at the same persistent logical location.'
                : 'This capture is not linked to a logical location yet — confirm a match to build its history.'}
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="timetravel-timeline">
              {(hist?.history || []).map((h) => {
                const isCurrent = h.capturePointId === capturePoint.id;
                const isSel = sel && sel.capturePointId === h.capturePointId;
                return (
                  <button key={h.capturePointId} onClick={() => !isCurrent && openCompare(h)} disabled={isCurrent}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg border text-left ${isCurrent ? 'bg-sky-600 text-white border-sky-600' : isSel ? 'border-sky-400 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`timeline-point-${(h.capturedAt || '').slice(0, 10)}`}>
                    <div className="text-xs font-semibold">{(h.capturedAt || '').slice(0, 10)}</div>
                    <div className={`text-[10px] ${isCurrent ? 'text-sky-100' : 'text-slate-400'}`}>{h.walkNumber || ''}{isCurrent ? ' · current' : ''}</div>
                  </button>
                );
              })}
              {(hist?.history || []).length <= 1 && <span className="text-xs text-slate-400">No other historical captures at this location.</span>}
            </div>
          </div>

          {/* Comparison */}
          {cmp && (
            <div data-testid="comparison-view">
              {/* Governance banner */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`text-[11px] px-2 py-1 rounded-full ${CONF_STYLE[cmp.confidence]}`} data-testid="match-confidence-badge">
                  {cmp.confidence} confidence
                </span>
                <span className="text-xs text-slate-600 flex items-center gap-1" data-testid="match-explanation"><GitCompare className="w-3.5 h-3.5" /> {MATCH_LABEL[cmp.matchType] || cmp.matchType} — {cmp.explanation}</span>
              </div>
              {!cmp.overlayAllowed && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 flex items-center gap-1" data-testid="overlay-disabled-note">
                  <Info className="w-3.5 h-3.5" /> Overlay disabled — {cmp.overlayDisabledReason} View synchronized (orientation only, not geometric registration).
                </div>
              )}
              {cmp.drawingRevisionWarning && (
                <div className="text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-1 mb-2 flex items-center gap-1" data-testid="drawing-revision-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> {cmp.drawingRevisionWarning}
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-600">
                  <input type="checkbox" checked={sync} onChange={(e) => setSync(e.target.checked)} data-testid="sync-view-toggle" /> Sync View (orientation)
                </label>
                <div className="flex items-center gap-2">
                  <button onClick={analyzeChange} disabled={aiBusy} className="text-[11px] flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-violet-300 text-violet-700 disabled:opacity-50" data-testid="analyze-visible-change-btn">
                    {aiBusy ? <span className="animate-pulse">Analyzing…</span> : <><GitCompare className="w-3.5 h-3.5" /> Analyze Visible Change</>}
                  </button>
                  {isMobile && (
                    <div className="flex rounded-lg border overflow-hidden text-xs" data-testid="mobile-ab-toggle">
                      <button onClick={() => setMobileSide('A')} className={`px-3 py-1 ${mobileSide === 'A' ? 'bg-sky-600 text-white' : ''}`}>Date A</button>
                      <button onClick={() => setMobileSide('B')} className={`px-3 py-1 ${mobileSide === 'B' ? 'bg-sky-600 text-white' : ''}`}>Date B</button>
                    </div>
                  )}
                </div>
              </div>
              {aiOut && (
                <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1 mb-2 flex items-center gap-1" data-testid="analyze-visible-change-result">
                  <Info className="w-3.5 h-3.5" /> {aiOut === 'AI_UNAVAILABLE'
                    ? 'AI service not configured — visual change analysis is unavailable. Manual A/B comparison remains fully usable.'
                    : `AI change proposal created (${aiOut}) — review it in the AI Review Queue. AI is a proposal, not authoritative.`}
                </div>
              )}

              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                {(!isMobile || mobileSide === 'A') && (
                  <Pane label="Date A" pane={cmp.paneA} projectId={projectId}
                    viewer={<Sphere360Viewer imageUrl={panoFile(projectId, cmp.paneA.panoramaId)}
                      onView={sync ? (v) => setViewB({ ...v, nonce: Date.now() }) : undefined}
                      viewOverride={sync ? viewA : undefined} />} testid="pane-a" />
                )}
                {(!isMobile || mobileSide === 'B') && (
                  <Pane label="Date B" pane={cmp.paneB} projectId={projectId}
                    viewer={<Sphere360Viewer imageUrl={panoFile(projectId, cmp.paneB.panoramaId)}
                      onView={sync ? (v) => setViewA({ ...v, nonce: Date.now() }) : undefined}
                      viewOverride={sync ? viewB : undefined} />} testid="pane-b" />
                )}
              </div>
            </div>
          )}
          {!cmp && others.length > 0 && <p className="text-sm text-slate-400 text-center py-6">Select a date on the timeline to compare it with the current capture.</p>}
        </div>
      </div>
    </div>
  );
}

const Pane = ({ label, pane, viewer, testid }) => (
  <div className="border border-slate-200 rounded-lg p-2" data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-bold text-slate-700">{label} · {(pane.capturedAt || '').slice(0, 10)}</span>
      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{pane.registrationType === 'FLOOR_PLAN_POSITIONED' ? 'Floor-plan positioned' : 'Context-linked'}</span>
    </div>
    {viewer}
    <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-2">
      <span>{pane.walkNumber}</span>
      {pane.drawingRevision?.drawingNumber && <span className="flex items-center gap-0.5"><Link2 className="w-2.5 h-2.5" /> {pane.drawingRevision.drawingNumber} rev {pane.drawingRevision.revision}</span>}
    </div>
  </div>
);
